import useSessionTimeout from "~/services/session/SessionTimeOut";
import { SIMPLMenuHeader } from "../../../../components/commons/header/SIMPLMenuHeader";
import { Layout } from "../../../../components/commons/layout/Layout";
import { SIMPLTitle } from "../../../../components/commons/title/SIMPLTitle";
import { ChangeEvent, useEffect, useState } from "react";
import { PropRow, SortTable } from "~/models/Commons ";
import { LABELS } from "~/resources/Labels";
import { buildLogoutUrl } from "~/services/keycloak";
import { SIMPLTable } from "../../../../components/commons/table/SimplTable";
import { SIMPLButtonCustomized } from "../../../../components/commons/button/SIMPLButtonCustumized";
import AddIcon from "@mui/icons-material/Add";
import { VmComponentsHeader } from "~/models/vmtemplate/ComponentsHeader";
import {
  attachResourcesToComponentes,
  displayEntries,
  sortScripts,
} from "~/Utils/Utils";
import { Entries } from "~/models/enums/Entries";
import { PayloadType } from "~/models/types/Payload";
import { useGetComponentsQuery } from "~/services/vm/Components";
import { AddVmPostProvisioning } from "./AddVmPostProviosing";
import { LoadingWrapper } from "../../../../components/commons/spiner/SimpleLoaderWrapper";
import { ViewContentModal } from "../ViewContentModal";

export const SimplVmPostProviosing = () => {
  const [active] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [size, setSize] = useState<number>(5);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [type] = useState<string>("postProvisioning");
  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT);
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(
    import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN
  );
  const { data, refetch,isFetching,isLoading,isError } = useGetComponentsQuery({ page, size, active, type });
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  /**
   * Initializes session timeout handling.
   *
   * @param {Object} config - Configuration for session timeout.
   * @param {number} config.inactivityLimit - Time in ms before user is considered inactive.
   * @param {number} config.forcedLogoutLimit - Time in ms before forced logout after inactivity.
   * @param {Function} config.buildLogoutUrl - Function that returns the logout URL.
   * @param {Storage} config.storage - Storage mechanism (e.g., sessionStorage).
   * */
  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    storage: sessionStorage,
  });

  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };

  /**
   * Initializes and sets the table rows by:
   * 1. Filtering scripts based on the active tab.
   * 2. Sorting the filtered scripts according to the sort configuration.
   * 3. Mapping sorted scripts to table row format and setting rows.
   */
  const initComponents = () => {
    // Ensure payload exists before processing
    const payload = data?.payload as PayloadType | undefined;

    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }
    // Attach resources, sort scripts, and customize entries
    const componentMergedList = attachResourcesToComponentes(payload);
    const sortedScripts = sortScripts(componentMergedList, sortConfig);
    const customizedList = displayEntries(
      sortedScripts,
      totalElements,
      Entries.Twenty
    );
    // Set pagination visibility and total elements count
    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));

    if (payload?.page?.totalElements) {
      setTotalElements(payload?.page?.totalElements);
    }

    // Process rows and set them
    const scriptRows: PropRow[] = customizedList.map((scriptItem: any) => ({
      row: scriptItem,
    }));

    setRows(scriptRows);
    setShowPagination(true);
  };

  useEffect(() => {
    initComponents();
  }, [data, active, sortConfig, size, total, totalElements]);

  /**
   * @effect
   * - Runs when the 'active' dependency changes.
   * - Calls setPage(1) to reset the pagination.
   */
  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [active, size]);

  const renderSIMPLTitle = () => (
    <SIMPLTitle title={"Post Provisioning Management"} />
  );
  const handleCloseAddModal = () => setIsAddModalOpen(false);
  const handleViewContent = (row: any) => setSelectedItem(row);
  const handleCloseViewContent = () => setSelectedItem(null);

  const showFilterHandler = () => setShowFilter((prev) => !prev);

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };
  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };
  const handlePageChange = (page: number) => {
    setPage(page);
  };
  /* Renders the "Add Script" button */
  const renderButtonAddProvider = () => (
    <SIMPLButtonCustomized
      label={LABELS.AddPost}
      buttonHandlerAction={() => setIsAddModalOpen(true)}
      firstIcon={<AddIcon fontSize="small" />}
      secondIcon={<AddIcon fontSize="small" />}
      secondary
    />
  );
  const renderAddProviderModal = () =>
    isAddModalOpen && (
      <AddVmPostProvisioning
        onCloseModal={handleCloseAddModal}
        editedItem={null}
        setConfig={setSortConfig}
        title={LABELS.PostProviosing}
        refetch={refetch}
      />
    );

  const renderSIMPLTable = () => (
    <div className="flex flex-col w-full">
      <div className="mt-5">
        <div className="container mx-auto mt-10">
          <SIMPLTable
            clear={handleResetAll}
            button={renderButtonAddProvider()}
            page={page}
            total={total}
            totalElements={totalElements}
            showPagination={showPagination}
            active={true}
            showHandler={showFilterHandler}
            filterActive={showFilter}
            tableHeaders={VmComponentsHeader}
            rows={rows}
            onChangePage={handlePageChange}
            onChange={handleSizeChange}
            sortConfig={sortConfig}
            handleSort={handleSort}
            chipLabels={[]}
            showButtonDetails
            handleOpenDetail={handleViewContent}
          />
        </div>
      </div>
    </div>
  );
  return (
    <SIMPLMenuHeader>
      <Layout>
        {renderSIMPLTitle()}
        {renderAddProviderModal()}
        {selectedItem && (
          <ViewContentModal onCloseModal={handleCloseViewContent} item={selectedItem} />
        )}
           <LoadingWrapper
              loadingTitle='Loading ..' 
              isLoading={isLoading || isFetching} 
              isError={isError}>
              {renderSIMPLTable()}
              </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
